import React from 'react'
import {
    SearchStream,
    HomeStream,
    PrivateStream,
    NotificationStream,
} from 'components/filters/FilterIcon'
import { Circle } from 'lucide-react'
import cn from 'classnames'
import useStore from 'state/knovStore'
import { openCenter } from 'components/filters/useStreamFilters'
import styles from 'components/panels/control.module.scss'
import WithTooltip from 'components/shared/WithTooltip'
import { isMobile } from 'lib/device'
import { getCenterPanel } from 'state/imperativeApis/swiperApi'
import { homePrivateEnabled } from 'lib/homePrivate'

export function FloatingSignupButton() {
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)

    return (
        <div
            data-floating-signup
            className={cn(
                styles.floatingSignupContainer,
                isMobile && styles.mobileFloatingSignupContainer,
            )}
        >
            <button
                type="button"
                className={styles.floatingSignupButton}
                onClick={() => openAuthModal('signup', 'mobile_bottom_join')}
            >
                Join
            </button>
        </div>
    )
}

export default function Control({
    showFilterOptions,
    setShowFilterOptions,
    isIOSStandalone,
    isiOSWithHomeIndicator,
    // The keyboard has just gone and the navbar is coming back: it rises from
    // the bottom edge rather than cutting in (mobileControlReturn).
    returning = false,
}) {
    useStore(state => state.activeIndex)
    const numPanels = useStore(state => state.numPanels)
    const onePanel = numPanels === 1
    const panel = getCenterPanel()
    const filter = panel?.filter
    const getSelectedClass = condition => (condition ? styles.selectedIcon : null)
    const streamsScope = filter?.streams_scope
    const homeSelectedStyles = getSelectedClass(
        filter?.public || filter?.following || (homePrivateEnabled() && filter?.private),
    )
    const privateSelectedStyles = getSelectedClass(filter?.private || streamsScope === 'private')
    const searchSelectedStyles = getSelectedClass(filter?.search)
    const notificationsSelectedStyles = getSelectedClass(filter?.notifications)

    const onSelectOrdinals = ev => {
        ev.preventDefault()
        ev.stopPropagation()
        openCenter({ ordinals: true, order: 'time' })
    }

    return (
        <div>
            <div
                data-bottom-navigation
                className={cn(
                    styles.lowerLevelContainer,
                    isMobile && styles.mobileControl,
                    isMobile &&
                        isIOSStandalone &&
                        !isiOSWithHomeIndicator &&
                        styles.mobileControlIOSStandalone,
                    isMobile &&
                        isIOSStandalone &&
                        isiOSWithHomeIndicator &&
                        styles.mobileControlIOSStandaloneNotch,
                    isMobile && returning && styles.mobileControlReturning,
                    onePanel && !isMobile && styles.onePanelLowerLevelContainer,
                )}
            >
                <div
                    className={cn(styles.publicControlContainer, isMobile && styles.mobileNavItem)}
                >
                    <div className={styles.publicControl}>
                        <WithTooltip tip="Home" place="top">
                            <HomeStream
                                filter={filter}
                                panel={panel}
                                contextStyles={cn(styles.publicControl)}
                                useInsertCenter={true}
                                append
                                selectedStyles={homeSelectedStyles}
                            />
                        </WithTooltip>
                    </div>
                </div>

                {/* home_private: Private moves onto the Home surface
                    as a scope tab, so the navbar drops its dedicated control. */}
                {!homePrivateEnabled() && (
                    <div
                        className={cn(
                            styles.privateControlContainer,
                            isMobile && styles.mobileNavItem,
                        )}
                    >
                        <div className={styles.privateControl}>
                            <WithTooltip tip="Private" place="top">
                                <PrivateStream
                                    filter={filter}
                                    panel={panel}
                                    contextStyles={cn(styles.privateControl, styles.controlIcon)}
                                    useInsertCenter={true}
                                    isControl={true}
                                    append
                                    selectedStyles={privateSelectedStyles}
                                />
                            </WithTooltip>
                        </div>
                    </div>
                )}

                <div className={cn(styles.controlGroup, isMobile && styles.mobileNavItem)}>
                    <div className={styles.streamControlContainer}>
                        <WithTooltip tip="Ordinals" place="top">
                            <div onClick={onSelectOrdinals}>
                                <div
                                    className={cn(
                                        styles.ordinalsAppChip,
                                        filter?.ordinals &&
                                            !filter?.market &&
                                            styles.ordinalsAppChipSelected,
                                    )}
                                >
                                    <Circle />
                                </div>
                            </div>
                        </WithTooltip>
                    </div>
                </div>

                <div className={cn(styles.searchStreamContainer, isMobile && styles.mobileNavItem)}>
                    <SearchStream
                        filter={filter}
                        panel={panel}
                        contextStyles={cn(styles.searchIcon, styles.controlIcon)}
                        tip="Search"
                        place="top"
                        useInsertCenter={true}
                        append
                        selectedStyles={searchSelectedStyles}
                    />
                </div>

                {gon?.currentUser && (
                    <div
                        className={cn(styles.notiStreamContainer, isMobile && styles.mobileNavItem)}
                    >
                        <NotificationStream
                            filter={filter}
                            panel={panel}
                            contextStyles={cn(styles.notiIcon)}
                            canActive
                            activeStyles={styles.notiActive}
                            showIcon
                            tip="Notifications"
                            place="top"
                            append
                            showNumNoti
                            selectedStyles={notificationsSelectedStyles}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
